import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { useLocation } from "react-router-dom";
import GoldRatesWidget from "@/components/GoldRatesWidget";
import MakingChargeCalculator from "@/components/MakingChargeCalculator";
import { useSettings } from "@/context/SettingsContext";
import { whatsappLink } from "@/lib/api";
import { MessageCircle, TrendingUp, Sparkles, Users, CheckCircle2 } from "lucide-react";

export function GoldRatesPage() {
    const { rates } = useSettings();
    return (
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
            <div className="text-center mb-12">
                <div className="eyebrow">Today's Prices</div>
                <h1 className="font-serif-luxe text-5xl md:text-6xl mt-3">Gold & Silver Rates</h1>
                <div className="w-16 h-[1px] bg-[color:var(--champagne)] mx-auto mt-5" />
                <p className="text-sm text-[color:var(--muted-brown)] mt-4">Last updated: {rates.updated_at ? new Date(rates.updated_at).toLocaleString("en-IN") : "—"}</p>
            </div>
            <GoldRatesWidget />
            <div className="mt-8 max-w-2xl mx-auto text-center">
                <p className="text-sm text-[color:var(--muted-brown)] leading-relaxed">
                    Rates shown are indicative per gram, subject to change through the day. For today's final rate, weight-specific pricing, or your desired jewellery piece, please confirm with Singh Jewellers.
                </p>
            </div>

            <div className="mt-16 max-w-3xl mx-auto">
                <MakingChargeCalculator />
            </div>

            <div className="mt-16 grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
                <div className="card-luxe p-6 text-center"><TrendingUp className="mx-auto text-[color:var(--champagne)] mb-3" /><div className="font-serif-luxe text-lg">Live Rates</div><div className="text-xs text-[color:var(--muted-brown)] mt-2">Refreshed regularly by our team.</div></div>
                <div className="card-luxe p-6 text-center"><Sparkles className="mx-auto text-[color:var(--champagne)] mb-3" /><div className="font-serif-luxe text-lg">Pure Metal Only</div><div className="text-xs text-[color:var(--muted-brown)] mt-2">Transparent purity, always disclosed.</div></div>
                <div className="card-luxe p-6 text-center"><Users className="mx-auto text-[color:var(--champagne)] mb-3" /><div className="font-serif-luxe text-lg">Honest Making Charges</div><div className="text-xs text-[color:var(--muted-brown)] mt-2">Competitive, and always transparent.</div></div>
            </div>
        </div>
    );
}

export function PlanPage() {
    const loc = useLocation();
    const slug = loc.pathname.includes("kitty") ? "kitty" : "gold_harvest";
    const [plan, setPlan] = useState(null);
    const { settings } = useSettings();

    useEffect(() => {
        client.get(`/plans/${slug}`).then(r => setPlan(r.data)).catch(() => setPlan(null));
        window.scrollTo(0, 0);
    }, [slug]);

    if (!plan) return <div className="max-w-3xl mx-auto py-24 text-center font-serif-luxe text-2xl">Loading…</div>;

    const heroImg = slug === "gold_harvest"
        ? "https://images.unsplash.com/photo-1611085583191-a3b181a88401?w=1800"
        : "https://images.unsplash.com/photo-1600685890506-593fdf55949b?w=1800";

    const wa = whatsappLink(settings.whatsapp_number, `Hello Singh Jewellers, I would like to enquire about the ${plan.title}. Please share details.`);

    return (
        <div>
            <section className="relative h-[60vh] overflow-hidden">
                <img src={heroImg} alt={plan.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[color:var(--charcoal)]/60" />
                <div className="absolute inset-0 flex items-center">
                    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 w-full">
                        <div className="eyebrow text-[color:var(--champagne-light)]">SINGH JEWELLERS</div>
                        <h1 className="font-serif-luxe text-5xl md:text-7xl text-white mt-3">{plan.title}</h1>
                        <p className="font-serif-luxe italic text-white/80 text-2xl mt-3">{plan.subtitle}</p>
                    </div>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
                <p className="text-lg leading-relaxed text-[color:var(--charcoal)] font-serif-luxe italic">{plan.description}</p>
            </section>

            <section className="max-w-[1440px] mx-auto px-6 lg:px-10 pb-16">
                <div className="text-center mb-10">
                    <div className="eyebrow">The Journey</div>
                    <h2 className="font-serif-luxe text-4xl mt-3">How It Works</h2>
                </div>
                <div className="grid md:grid-cols-4 gap-5">
                    {plan.how_it_works.map((step, i) => (
                        <div key={i} className="card-luxe p-6 text-center">
                            <div className="font-serif-luxe text-4xl text-[color:var(--champagne)] mb-3">0{i+1}</div>
                            <p className="text-sm text-[color:var(--charcoal)] leading-relaxed">{step}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="bg-[color:var(--beige)]/40 py-16">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-10">
                    <div>
                        <div className="eyebrow mb-3">Benefits</div>
                        <h3 className="font-serif-luxe text-3xl mb-6">Why This Plan</h3>
                        <ul className="space-y-3">
                            {plan.benefits.map((b, i) => (
                                <li key={i} className="flex gap-3 text-sm text-[color:var(--charcoal)]"><CheckCircle2 size={18} className="text-[color:var(--champagne)] flex-shrink-0" />{b}</li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="eyebrow mb-3">Contributions</div>
                        <h3 className="font-serif-luxe text-3xl mb-6">Monthly Options</h3>
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            {plan.monthly_options.map(m => (
                                <div key={m} className="card-luxe p-4 text-center font-serif-luxe text-xl">{m}</div>
                            ))}
                        </div>
                        <div className="text-sm text-[color:var(--muted-brown)]">Duration: {plan.duration}</div>
                    </div>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
                <div className="eyebrow mb-3">Frequently Asked</div>
                <h3 className="font-serif-luxe text-3xl mb-8">Questions Families Ask</h3>
                <div className="space-y-4">
                    {plan.faqs.map((f, i) => (
                        <details key={i} className="card-luxe p-5">
                            <summary className="cursor-pointer font-serif-luxe text-lg">{f.q}</summary>
                            <p className="text-sm text-[color:var(--muted-brown)] mt-3 leading-relaxed">{f.a}</p>
                        </details>
                    ))}
                </div>
                <div className="mt-10">
                    <div className="eyebrow mb-2">Terms</div>
                    <ul className="text-xs text-[color:var(--muted-brown)] space-y-1.5 list-disc pl-4">
                        {plan.terms.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                </div>
            </section>

            <section className="bg-[color:var(--warm-black)] text-white py-16">
                <div className="max-w-3xl mx-auto text-center px-6">
                    <h3 className="font-serif-luxe text-3xl md:text-4xl mb-4">Ready to begin?</h3>
                    <p className="text-white/70 mb-8">Speak with our team and start your jewellery journey today.</p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href={wa} target="_blank" rel="noreferrer" className="btn-whatsapp"><MessageCircle size={16} /> WhatsApp Us</a>
                        <a href={`tel:${settings.phone}`} className="btn-gold">Call Now</a>
                    </div>
                </div>
            </section>
        </div>
    );
}
